// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import xProductPartNumbersStyles from "@werner/App/SharedStyle/ProductPartNumbersStyles";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    productNumber: string;
    customerProductNumber?: string;
    manufacturerItem?: string;
    showCustomerName?: boolean;
    showLabel?: boolean;
    showManufacturerItem?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
    showProductNumber?: boolean;
    // XNG-Changes
    replacementPartNumber?: string;
    isShareEntity?: boolean;
    addedOn?: string;
    lastOrderDate?: string;
}

type Props = OwnProps;

export interface ProductPartNumbersStyles {
    condensed?: boolean;
    container?: GridContainerProps;
    erpNumberGridItem?: GridItemProps;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;
    customerNameGridItem?: GridItemProps;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;
    manufacturerItemGridItem?: GridItemProps;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;
    replacementNumberLabelText?: TypographyPresentationProps;
    replacementNumberValueText?: TypographyPresentationProps;
    addedOnLabelText?: TypographyPresentationProps;
    addedOnValueText?: TypographyPresentationProps;
    lastOrderDateLabelText?: TypographyPresentationProps;
    lastOrderDateValueText?: TypographyPresentationProps;
}

export const productPartNumbersStyles: ProductPartNumbersStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 2px;
        `,
    },
    erpNumberLabelText: {
        ...xProductPartNumbersStyles.label,
    },
    erpNumberValueText: {
        ...xProductPartNumbersStyles.value,
    },
    customerNameLabelText: {
        ...xProductPartNumbersStyles.label,
    },
    customerNameValueText: {
        ...xProductPartNumbersStyles.value,
    },
    manufacturerItemLabelText: {
        ...xProductPartNumbersStyles.label,
    },
    manufacturerItemValueText: {
        ...xProductPartNumbersStyles.value,
    },
    addedOnLabelText: {
        ...xProductPartNumbersStyles.label,
    },
    addedOnValueText: {
        ...xProductPartNumbersStyles.value,
    },
    lastOrderDateLabelText: {
        ...xProductPartNumbersStyles.label,
    },
    lastOrderDateValueText: {
        ...xProductPartNumbersStyles.value,
    },
};

const ProductPartNumbers: FC<Props> = ({
    productNumber,
    customerProductNumber,
    manufacturerItem,
    showCustomerName = true,
    showManufacturerItem = true,
    showLabel = true,
    extendedStyles,
    showProductNumber = true,
    replacementPartNumber,
    isShareEntity = false,
    addedOn,
    lastOrderDate,
}) => {
    const [styles] = React.useState(() => mergeToNew(productPartNumbersStyles, extendedStyles));
    const displayManufacturerNumber = showManufacturerItem && manufacturerItem;
    const displayCustomerNumber = showCustomerName && customerProductNumber;
    const displayReplacementPartNumber = !!replacementPartNumber;
    // const numberOfItems = 1 + (displayManufacturerNumber ? 1 : 0) + (displayCustomerNumber ? 1 : 0);
    // const mobileGridItemWidth = numberOfItems > 1 ? 6 : 12;
    // const desktopGridItemWidth = 12 / numberOfItems;
    // const gridItemWidth = styles.condensed
    //     ? [mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, desktopGridItemWidth]
    //     : 12;

    // <XNG-Changes>
    if (isShareEntity) {
        return (
            <GridContainer gap={4}>
                <GridItem width={12}>
                    <StyledWrapper {...styles.manufacturerItemGridItem}>
                        <Typography {...styles.manufacturerItemLabelText}>{translate("MFR #")}</Typography>
                        <Typography {...styles.manufacturerItemValueText} data-test-selector="manufacturerItem">
                            {manufacturerItem}
                        </Typography>
                    </StyledWrapper>
                </GridItem>
                {showProductNumber && (
                    <GridItem width={12}>
                        <StyledWrapper {...styles.erpNumberGridItem}>
                            <Typography {...styles.erpNumberLabelText}>{translate("Werner #")}</Typography>
                            <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                                {productNumber}
                            </Typography>
                        </StyledWrapper>
                    </GridItem>
                )}
                {displayCustomerNumber && (
                    <GridItem width={12}>
                        <StyledWrapper {...styles.customerNameGridItem}>
                            <Typography {...styles.customerNameLabelText}>{translate("Customer Part #")}</Typography>
                            <Typography {...styles.customerNameValueText} data-test-selector="customerProductNumber">
                                {customerProductNumber}
                            </Typography>
                        </StyledWrapper>
                    </GridItem>
                )}
                {displayReplacementPartNumber && (
                    <GridItem width={12}>
                        <StyledWrapper {...styles.customerNameGridItem}>
                            <Typography {...styles.replacementNumberLabelText}>
                                {translate("Replacement Part #")}
                            </Typography>

                            <Typography
                                {...styles.replacementNumberValueText}
                                data-test-selector="replacementProductNumber"
                            >
                                {replacementPartNumber}
                            </Typography>
                        </StyledWrapper>
                    </GridItem>
                )}
            </GridContainer>
        );
    }
    // </XNG-Changes>

    // XNG-Changes: put MFG at top
    return (
        <StyledWrapper {...styles.container}>
            {displayManufacturerNumber && (
                <StyledWrapper {...styles.manufacturerItemGridItem}>
                    {showLabel && <Typography {...styles.manufacturerItemLabelText}>{translate("MFR #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.manufacturerItemLabelText}>{translate("MFR #")} </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.manufacturerItemValueText} data-test-selector="manufacturerItem">
                        {manufacturerItem}
                    </Typography>
                </StyledWrapper>
            )}
            {productNumber && showProductNumber && (
                <StyledWrapper {...styles.erpNumberGridItem}>
                    {/* XNG-Changes: change Part # to Werner # */}
                    {showLabel && <Typography {...styles.erpNumberLabelText}>{translate("Werner #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.erpNumberLabelText}>{translate("Werner #")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                        {productNumber}
                    </Typography>
                </StyledWrapper>
            )}
            {displayCustomerNumber && (
                <StyledWrapper {...styles.customerNameGridItem}>
                    {/* XNG-Changes: change My Part # to Customer Part # */}
                    {showLabel && (
                        <Typography {...styles.customerNameLabelText}>{translate("Customer Part #")}</Typography>
                    )}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.customerNameLabelText}>{translate("Customer Part #")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.customerNameValueText} data-test-selector="customerProductNumber">
                        {customerProductNumber}
                    </Typography>
                </StyledWrapper>
            )}
            {displayReplacementPartNumber && (
                <StyledWrapper {...styles.customerNameGridItem}>
                    {/* XNG-Changes: change My Part # to Customer Part # */}
                    {showLabel && (
                        <Typography {...styles.replacementNumberLabelText}>
                            {translate("Replacement Part #")}
                        </Typography>
                    )}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.replacementNumberLabelText}>
                                {translate("Replacement Part #")}
                            </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.replacementNumberValueText} data-test-selector="replacementProductNumber">
                        {replacementPartNumber}
                    </Typography>
                </StyledWrapper>
            )}
            {addedOn && (
                <StyledWrapper {...styles.customerNameGridItem}>
                    {showLabel && <Typography {...styles.addedOnLabelText}>{translate("Added On")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.addedOnLabelText}>{translate("Added On")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.addedOnValueText}>{addedOn}</Typography>
                </StyledWrapper>
            )}
            {lastOrderDate && (
                <StyledWrapper {...styles.customerNameGridItem}>
                    {showLabel && (
                        <Typography {...styles.lastOrderDateLabelText}>{translate("Last Order Date")}</Typography>
                    )}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.lastOrderDateLabelText}>{translate("lastOrderDate")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.lastOrderDateValueText}>{lastOrderDate}</Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default ProductPartNumbers;
